import { login as apiLogin, register as apiRegister, logout as apiLogout, getUser } from './api';
import { User, UserRole } from '../types/user';

// Token storage key
const TOKEN_KEY = 'lima_auth_token';
const USER_KEY = 'lima_user';
const USER_FETCHED_KEY = 'lima_user_fetched_at';

// The stored copy of the user carries their permissions, so it cannot be kept for ever:
// an access change made on the Permissions page would only reach them the next time they
// signed in, which is up to 30 days. It is re-read from the server this often instead.
const USER_MAX_AGE_MS = 60 * 1000;

// True when the sign-in token's own expiry time has passed (read, not verified).
export const tokenExpired = (token: string): boolean => {
  try {
    const part = token.split('.')[1];
    if (!part) return false; // not a JWT: only the server can tell
    const json = JSON.parse(atob(part.replace(/-/g, '+').replace(/_/g, '/')));
    return typeof json.exp === 'number' && json.exp * 1000 <= Date.now();
  } catch {
    return false;
  }
};

// Get token from localStorage. An expired one counts as signed out straight away,
// so nothing signed-in is ever drawn for it.
export const getToken = (): string | null => {
  const token = localStorage.getItem(TOKEN_KEY);
  if (token && tokenExpired(token)) {
    removeToken();
    return null;
  }
  return token;
};

// Set token in localStorage
export const setToken = (token: string): void => {
  localStorage.setItem(TOKEN_KEY, token);
};

// Remove token from localStorage
export const removeToken = (): void => {
  localStorage.removeItem(TOKEN_KEY);
  localStorage.removeItem(USER_KEY);
  localStorage.removeItem(USER_FETCHED_KEY);
};

// Get user from localStorage
export const getUserFromStorage = (): User | null => {
  const userJson = localStorage.getItem(USER_KEY);
  return userJson ? JSON.parse(userJson) : null;
};

// Set user in localStorage
export const setUserInStorage = (user: User): void => {
  localStorage.setItem(USER_KEY, JSON.stringify(user));
  localStorage.setItem(USER_FETCHED_KEY, String(Date.now()));
};

// True while the stored copy is young enough to trust.
const storedUserIsFresh = (): boolean => {
  const at = Number(localStorage.getItem(USER_FETCHED_KEY) || 0);
  return at > 0 && Date.now() - at < USER_MAX_AGE_MS;
};

// Login user
export const login = async (email: string, password: string): Promise<User> => {
  const { token, user } = await apiLogin(email, password);
  setToken(token);
  setUserInStorage(user);
  return user;
};

// Register new client user
export const register = async (email: string, password: string, fullName?: string): Promise<User> => {
  const { token, user } = await apiRegister(email, password, fullName);
  setToken(token);
  setUserInStorage(user);
  return user;
};

// Logout user
export const logout = async (): Promise<void> => {
  const token = getToken();
  if (token) {
    try {
      await apiLogout();
    } catch (error) {
      console.error('Logout error:', error);
    }
  }
  removeToken();
};

// Check if user is authenticated
export const isAuthenticated = (): boolean => {
  return !!getToken();
};

// Get current user
export const getCurrentUser = async (forceRefresh = false): Promise<User | null> => {
  const token = getToken();
  if (!token) {
    removeToken(); // Clear any stale data
    return null;
  }
  
  try {
    const storedUser = getUserFromStorage();
    if (storedUser && !forceRefresh && storedUserIsFresh()) {
      return storedUser;
    }

    const user = await getUser(token);
    if (user) {
      setUserInStorage(user);
      return user;
    }

    // If we get here, something's wrong with the token
    removeToken();
    return null;
  } catch (error) {
    console.error('Get current user error:', error);
    // The server said no: signed out, whatever the stored copy says.
    const status = Number((error as any)?.status || 0);
    if (status >= 400 && status < 500 && status !== 429) {
      removeToken();
      return null;
    }
    // The server could not be reached. Keep working from the stored copy rather than
    // signing them out; it will be re-read on the next attempt.
    const fallback = getUserFromStorage();
    if (fallback) return fallback;
    // Don't call removeToken() here as handleAuthError will handle the cleanup
    return null;
  }
};

// Force refresh current user data
export const refreshCurrentUser = async (): Promise<User | null> => {
  return getCurrentUser(true);
};

// Get user role
export const getUserRole = (): UserRole | null => {
  const user = getUserFromStorage();
  return user ? user.role : null;
};

// Check if user has specific role
export const hasRole = (role: UserRole): boolean => {
  const user = getUserFromStorage();
  if (!user || !isAuthenticated()) {
    return false;
  }
  return user.role === role;
};

// Check if user is admin
export const isAdmin = (): boolean => {
  return hasRole('admin');
};

// Check if user is agent
export const isAgent = (): boolean => {
  return hasRole('agent');
};

// Check if user is worker
export const isWorker = (): boolean => {
  return hasRole('worker');
};

// Check if user is client
export const isClient = (): boolean => {
  return hasRole('client');
};